import React, { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { Field } from 'formik';

import { ActionStatus, isLoading, LoadingIndicator } from '@parapet/common';
import { InputText } from '@app/shared/components/InputText';
import {
  CountryCode,
  CountryCodeExtended,
  PhoneWithCountryCodeForm,
} from './models';
import {
  cacheCodes,
  formatUnicodeToHtml,
  getPhoneValidityMessage,
  loadCodesFromCache,
  loadContryCodesFromApi,
} from './utils';
import {
  selectStyles,
  CodesLoadingIndicator,
  SelectContainer,
  StyledPhoneWithCountryCodeInput,
  StyledSelectOption,
} from './styles';

export interface PhoneWithContryCodeInputState {
  status: ActionStatus;
  countryCodes?: CountryCode[];
  extendedCountryCodes?: CountryCodeExtended[];
}

export interface PhoneWithContryCodeInputProps {
  valueChange?: (
    value: string,
    error: string | null,
    countryCode?: CountryCodeExtended,
  ) => void;
}

export const PhoneWithContryCodeInput: React.FC<
  PhoneWithContryCodeInputProps
> = ({ valueChange }) => {
  const [hasFocus, setHasFocus] = useState(false);
  const [countryCodesState, setCountryCodesState] =
    useState<PhoneWithContryCodeInputState>({
      status: ActionStatus.default,
    });
  const [phoneForm, setPhoneForm] = useState<PhoneWithCountryCodeForm>({
    phone: '',
  });

  const loadCountryCodes = async () => {
    const fromCache = loadCodesFromCache();

    if (fromCache) {
      const defaultCode = fromCache.countryPhones[0];
      if (defaultCode) {
        setPhoneForm({
          phone: '',
          countryCode: defaultCode,
          selectedCode: defaultCode.code,
        });
        console.log('PhoneWithContryCodeInput > defaultCode', defaultCode);
      }
      return setCountryCodesState({
        countryCodes: fromCache.countryCodes,
        extendedCountryCodes: fromCache.countryPhones,
        status: ActionStatus.success,
      });
    }

    setCountryCodesState({ status: ActionStatus.loading });

    try {
      const res = await loadContryCodesFromApi();

      const defaultCode = res.countryPhones.find(
        (c) => c.abbreviation === 'US',
      );

      setPhoneForm((form) => ({
        ...form,
        countryCode: defaultCode,
        selectedCode: defaultCode?.code,
      }));

      setCountryCodesState({
        countryCodes: res.countryCodes,
        extendedCountryCodes: res.countryPhones,
        status: ActionStatus.success,
      });

      cacheCodes(res);
    } catch (err) {
      setCountryCodesState({ status: ActionStatus.failure });
    }
  };

  const areCodesLoading = useMemo(
    () => isLoading(countryCodesState?.status),
    [countryCodesState],
  );

  useEffect(() => {
    loadCountryCodes();
  }, []);

  const onPhoneChange = ({
    currentTarget: { value: phone },
  }: SyntheticEvent<HTMLInputElement>) => {
    let invalid = false;
    if (phoneForm.countryCode?.validationPattern) {
      const regex = new RegExp(phoneForm.countryCode?.validationPattern);
      // Prefix the phone with code for the regex to pass
      const withCode = `${phoneForm.selectedCode} ${phone}`;
      invalid = !regex.test(withCode);
    }
    const message = getPhoneValidityMessage(invalid, phoneForm);
    setPhoneForm((form) => ({ ...form, phone, invalid }));
    valueChange?.(phone, message, phoneForm.countryCode);
  };

  return (
    <div className="phone-input-group">
      <StyledPhoneWithCountryCodeInput hasFocus={hasFocus}>
        <SelectContainer>
          <Select<CountryCode>
            classNamePrefix="rc"
            aria-label="Select country code"
            placeholder="+ 1"
            maxMenuHeight={260}
            styles={selectStyles}
            isDisabled={areCodesLoading}
            isSearchable={false}
            closeMenuOnScroll={true}
            isOptionSelected={(o) =>
              o.abbreviation === phoneForm.countryCode?.abbreviation
            }
            options={countryCodesState?.countryCodes}
            value={countryCodesState?.countryCodes?.find(
              (c) => c.abbreviation === phoneForm.countryCode?.abbreviation,
            )}
            components={{
              ValueContainer: () => (
                <div>{phoneForm.selectedCode ?? '+ 1'}</div>
              ),
              Option: ({ innerProps, isSelected, data }) => (
                <StyledSelectOption
                  {...innerProps}
                  isSelected={isSelected}
                  isPrimary={
                    data.abbreviation === 'US' || data.abbreviation === 'CA'
                  }
                >
                  <span
                    dangerouslySetInnerHTML={{
                      __html: '&#' + formatUnicodeToHtml(data.flagUnicode),
                    }}
                  />
                  <strong>{data.code}</strong> {data.name}
                </StyledSelectOption>
              ),
            }}
            isClearable={false}
            onChange={(o) => {
              const extended = countryCodesState?.extendedCountryCodes?.find(
                (c) => c.abbreviation === o!.abbreviation,
              );
              if (!extended) {
                setPhoneForm((f) => ({ ...f, selectedCode: o!.code }));
                return console.log('No extended country code found for', o);
              }
              setPhoneForm((f) => ({
                ...f,
                selectedCode: o!.code,
                countryCode: extended,
              }));
            }}
          />
        </SelectContainer>
        <Field
          type="tel"
          name="phone"
          placeholder="Phone number"
          disabled={areCodesLoading}
          value={phoneForm.phone}
          as={InputText}
          tabIndex={1}
          onChange={onPhoneChange}
          onFocus={() => setHasFocus(true)}
          onBlur={() => setHasFocus(false)}
        />
        {areCodesLoading && (
          <CodesLoadingIndicator>
            <LoadingIndicator />
          </CodesLoadingIndicator>
        )}
      </StyledPhoneWithCountryCodeInput>

      {/* {phoneForm.countryCode && phoneForm.invalid && (
        <InvalidPattern>
          Invalid {phoneForm.countryCode.abbreviation} phone, format must be{' '}
          {phoneForm.countryCode?.format}
        </InvalidPattern>
      )} */}

      {countryCodesState.status === ActionStatus.failure && (
        <div className="error-message">
          Failed to load country codes. Please try again later.
        </div>
      )}
    </div>
  );
};
